import React, { useCallback, useMemo, useRef, useState } from 'react';
import { View } from 'react-native';

import { Avatar, Box, HStack, Text, VStack, theme } from '@squire/ui';

import type { BarberScheduleResponse } from '@/api/booking/getBookingScheduleQuery';
import { ExclamationMarkIcon } from '@/components/_icons';
import { ChooseTimeEmptyState } from '@/components/booking/ChooseTimeEmptyState';
import { ChooseTimeSlotGrid } from '@/components/booking/ChooseTimeSlotGrid';
import { WaitlistTimePreferencePicker } from '@/components/booking/WaitlistTimePreferencePicker';
import { useScreenTranslations } from '@/hooks/useScreenTranslations';
import { isBarberWorkingOnDate } from '@/utils/barberScheduleUtils';
import {
  DateFormat,
  formatDate,
  getTimePeriod,
  parseDate,
  parseDateWithTimezone,
} from '@/utils/dateUtils';

import { styles } from './styles';
import type { ChooseTimeGuestSectionProps } from './types';

export function ChooseTimeGuestSection(props: ChooseTimeGuestSectionProps) {
  const {
    index = 0,
    cartItem,
    guestSchedule,
    selectedDate,
    isExpanded,
    onTimeSelect,
    onToggleExpanded,
    isSingleAppointment,
    testID = 'choose-time-guest-section',
    timezone,
    onNavigateToNextAvailable,
    showSuggestedTimes = false,
    disabled = false,
    showWaitlist = false,
    onWaitlistPress,
    isWaitlistSelected = false,
    waitlistDisabled = false,
    barberScheduleInfo,
    isBarberScheduleLoading = false,
    standbyWaitlist,
  } = props;

  const { t, i18n } = useScreenTranslations('ChooseTimeGuestSection');

  const professionalName = cartItem.barber?.firstName || t('anyProfessional');
  const professionalPhotoUrl = cartItem.barber?.photos?.[0]?.url;

  // Parse selectedTime using the same timezone logic as the grid slots
  // to ensure accurate comparison
  const selectedTime = cartItem.dateTime
    ? timezone
      ? parseDateWithTimezone(cartItem.dateTime, timezone)
      : parseDate(cartItem.dateTime)
    : undefined;

  const serviceName = cartItem.subItems
    .map((service) => service.name)
    .join(' + ');

  const serviceDuration = cartItem.subItems.reduce(
    (total, service) => total + service.duration,
    0,
  );

  const dateKey = formatDate(selectedDate, DateFormat.API_DATE);

  const currentAvailability = guestSchedule.data?.find(
    (schedule: { day: string; availability: number }) =>
      selectedDate && schedule.day === dateKey,
  );

  // Cache the last valid availability to prevent flickering during refetch
  // When fetching, keep showing the previous slots until new data arrives
  const lastValidAvailabilityRef = useRef<BarberScheduleResponse | undefined>(
    undefined,
  );

  // Update the cache when we have valid availability data and not fetching
  if (currentAvailability && !guestSchedule.isFetching) {
    lastValidAvailabilityRef.current = currentAvailability;
  }

  // Use current availability if available, otherwise fall back to cached value during fetch
  const availability =
    currentAvailability ||
    (guestSchedule.isFetching ? lastValidAvailabilityRef.current : undefined);

  // Whether the barber is actually scheduled to work on selectedDate,
  // derived from the barber's day-off/day-on overrides and recurring
  // schedule (see isBarberWorkingOnDate) rather than from the guest's
  // time-slot response, which doesn't reliably distinguish "day off" from
  // "fully booked."
  const barberWorksOnDay = useMemo(() => {
    if (isBarberScheduleLoading) return false;
    return isBarberWorkingOnDate(barberScheduleInfo, selectedDate);
  }, [isBarberScheduleLoading, barberScheduleInfo, selectedDate]);

  const isAnyProfessional = cartItem.bookedWithAnyBarber || !cartItem.barber;
  // Standby reads the schedule on hand, so a background refetch can't hide
  // the button under an open picker; a first load has no schedule yet.
  const standbyDayIsWorked = isAnyProfessional
    ? standbyWaitlist?.anyProfessionalWorksOnDay
    : isBarberWorkingOnDate(barberScheduleInfo, selectedDate);
  const waitlistDayIsWorked = standbyWaitlist
    ? !!standbyDayIsWorked
    : barberWorksOnDay;

  const availabilityCount = Object.values(availability?.times || []).reduce(
    (acc, time) => {
      // Count slots that are available or selected (for group appointments)
      const availableTime = time.filter(
        (_time: { available: boolean; selected?: boolean }) =>
          _time.available || _time.selected,
      );

      return acc + availableTime.length;
    },
    0,
  );

  const formattedDate = formatDate(
    selectedDate,
    DateFormat.FULL_DATE,
    i18n.language,
  );

  const durationText = useMemo(
    () =>
      serviceDuration >= 60
        ? `${Math.floor(serviceDuration / 60)} ${serviceDuration >= 120 ? t('duration.hoursPlural') : t('duration.hours')}`
        : `${serviceDuration} ${t('duration.minutes')}`,
    [serviceDuration, t],
  );

  const availabilityText =
    availabilityCount === 0
      ? t('noTimesAvailable')
      : availabilityCount === 1
        ? t('oneTimeAvailable')
        : t('timesAvailable', { count: availabilityCount });

  const hasNoTimes = useMemo(() => {
    if (!availability) return true;

    // Check if there are any available or selected times across all periods.
    // For group appointments, a slot might be unavailable but still shown if
    // it's selected by this appointment.
    const hasAvailableMorning = availability.times.morning.some(
      (slot) => slot.available || slot.selected,
    );
    const hasAvailableAfternoon = availability.times.afternoon.some(
      (slot) => slot.available || slot.selected,
    );
    const hasAvailableEvening = availability.times.evening.some(
      (slot) => slot.available || slot.selected,
    );

    return (
      !hasAvailableMorning && !hasAvailableAfternoon && !hasAvailableEvening
    );
  }, [availability]);

  // Don't show empty state while fetching - data might be temporarily unavailable
  // during query key changes with keepPreviousData
  const showEmptyState = hasNoTimes && !guestSchedule.isFetching;

  // Wrap suggested times into BarberScheduleResponse format for ChooseTimeSlotGrid
  const suggestedTimesAvailability = useMemo(():
    | BarberScheduleResponse
    | undefined => {
    if (
      !showSuggestedTimes ||
      !availability?.suggestedTimes ||
      availability.suggestedTimes.length === 0
    ) {
      return undefined;
    }

    // Convert the flat suggestedTimes array into the bucketed times format
    // expected by ChooseTimeSlotGrid. The grid derives each slot's icon from
    // the bucket it lives in, so we must partition by the slot's actual time of
    // day (parsed in the shop timezone, matching how the grid parses slots and
    // how the API buckets the regular times). See SQR-13453.
    const times = {
      morning: [] as typeof availability.suggestedTimes,
      afternoon: [] as typeof availability.suggestedTimes,
      evening: [] as typeof availability.suggestedTimes,
    };

    availability.suggestedTimes.forEach((slot) => {
      const parsedTime = timezone
        ? parseDateWithTimezone(slot.time, timezone)
        : parseDate(slot.time);
      times[getTimePeriod(parsedTime)].push(slot);
    });

    return {
      day: availability.day,
      availability: availability.availability,
      suggestedTimes: [],
      times,
    };
  }, [showSuggestedTimes, availability, timezone]);

  // Track which section the time was selected from to avoid duplicate highlighting
  // Initialize based on whether there's already a selected time (from cart)
  const [selectedSource, setSelectedSource] = useState<
    'suggested' | 'available' | null
  >(() => (cartItem.dateTime ? 'available' : null));

  const handleSuggestedTimeSelect = useCallback(
    (time: Date, barberId?: string) => {
      setSelectedSource('suggested');
      onTimeSelect(time, barberId);
    },
    [onTimeSelect],
  );

  const handleAvailableTimeSelect = useCallback(
    (time: Date, barberId?: string) => {
      setSelectedSource('available');
      onTimeSelect(time, barberId);
    },
    [onTimeSelect],
  );

  return (
    <VStack testID={testID} style={{ paddingBottom: theme.spacing[8] }}>
      {/* Date & Availability Info */}
      {index === 0 && (
        <VStack space={theme.spacing[2]}>
          <Text variant="lStrong" color="textPrimary">
            {formattedDate}
          </Text>
        </VStack>
      )}

      {/* Professional & Service Info */}
      {!isSingleAppointment && (
        <HStack space={theme.spacing[3]} centerVertical style={styles.spaceTop}>
          <View>
            <Avatar
              size="md"
              shape="square"
              uri={professionalPhotoUrl}
              fallbackText={professionalName}
            />

            {!isSingleAppointment && showEmptyState && (
              <View style={styles.noAvailabilityIcon}>
                <ExclamationMarkIcon
                  color="textStatic"
                  size={10}
                  strokeWidth={2}
                />
              </View>
            )}
          </View>

          <VStack flex space={theme.spacing[1]}>
            <Text variant="lStrong" color="textPrimary">
              {professionalName}
            </Text>
            <HStack style={styles.spaceBetween}>
              <Text
                variant="m"
                color="textSecondary"
                numberOfLines={1}
                ellipsizeMode="tail"
                style={styles.serviceName}
              >
                {!isSingleAppointment && !showEmptyState
                  ? serviceName
                  : t('noAvailableTimes')}
              </Text>

              {!showEmptyState && (
                <Text
                  variant="m"
                  color="textSecondary"
                  numberOfLines={1}
                  style={styles.duration}
                >
                  {durationText}
                </Text>
              )}
            </HStack>
          </VStack>
        </HStack>
      )}

      {/* Suggested Times Section */}
      {suggestedTimesAvailability && (
        <VStack
          space={theme.spacing[1]}
          style={{ paddingTop: theme.spacing[3] }}
        >
          <Text variant="mStrong" color="textSecondary">
            {t('suggestedForYou')}
          </Text>
          <ChooseTimeSlotGrid
            timezone={timezone}
            availability={suggestedTimesAvailability}
            selectedTime={
              selectedSource === 'suggested' ? selectedTime : undefined
            }
            onTimeSelect={handleSuggestedTimeSelect}
            isExpanded={true}
            onToggleExpanded={() => {}}
            testID={`${testID}-suggested-slot-grid`}
            isSingleAppointment={true}
            disabled={disabled}
          />
        </VStack>
      )}

      {/* Time Slots or Empty State */}
      {/* In a group appointment, a barber with no available times only keeps
          the "no available times" note in the header above — no Next Time or
          Waitlist actions for that barber. */}
      {showEmptyState ? (
        isSingleAppointment && (
          <>
            <Box style={styles.spaceTop}>
              <ChooseTimeEmptyState
                type="no-times-today"
                professionalName={professionalName}
                testID={`${testID}-empty-state`}
                onNavigateToNextAvailable={onNavigateToNextAvailable}
                showWaitlist={
                  showWaitlist &&
                  waitlistDayIsWorked &&
                  !availability?.availability
                }
                onWaitlistPress={() =>
                  onWaitlistPress?.(cartItem.id, cartItem.barber?.id || '')
                }
                isWaitlistSelected={isWaitlistSelected}
                waitlistDisabled={waitlistDisabled}
                isStandbyWaitlist={!!standbyWaitlist}
              />
            </Box>
            {standbyWaitlist && isWaitlistSelected && (
              <Box style={styles.timePreferences}>
                <WaitlistTimePreferencePicker
                  selected={standbyWaitlist.timePreferences}
                  showAfterHours={standbyWaitlist.showAfterHours}
                  onChange={standbyWaitlist.onTimePreferencesChange}
                  disabled={disabled}
                  testID={`${testID}-time-preferences`}
                />
              </Box>
            )}
          </>
        )
      ) : (
        <VStack
          space={theme.spacing[3]}
          style={
            showSuggestedTimes && suggestedTimesAvailability && styles.spaceTop
          }
        >
          {isSingleAppointment && (
            <Text variant="m" color="textSecondary">
              {availabilityText}
            </Text>
          )}
          <ChooseTimeSlotGrid
            timezone={timezone}
            availability={
              availability as Exclude<typeof availability, undefined>
            }
            selectedTime={
              // When there are no suggested times, always show selected time in the available grid
              // When there are suggested times, only show if selected from this grid
              !showSuggestedTimes || selectedSource === 'available'
                ? selectedTime
                : undefined
            }
            onTimeSelect={handleAvailableTimeSelect}
            isExpanded={isExpanded}
            onToggleExpanded={onToggleExpanded}
            isLoading={guestSchedule.isLoading}
            testID={`${testID}-slot-grid`}
            isSingleAppointment={isSingleAppointment}
            disabled={disabled}
          />
        </VStack>
      )}
    </VStack>
  );
}
